import * as React from 'react';
import { useRef } from 'react';
import { Flex, View, Radio, RadioGroup, ComboBox, Item } from '@adobe/react-spectrum';
import { Key } from '@react-types/shared';
import { AtollonButton, AtollonButtonDesignType } from '../../../../ui/Button/AtollonButton';
import { NamesList, NameInfo, INamesListRef } from '../NamesList';
import { ColumnsSelector, ColumnsSelectorDesignType, IColumnsSelectorRef } from '../ColumnsSelector';
import { IColumnSection, IColumnItem } from '../ColumnsList';
import { ICustomViewData } from '../../CustomViewDialog';
import { Profile } from '../../../../../services/soap/access/responses/ListProfileResponse';
import ChevronDoubleRight from '@spectrum-icons/workflow/ChevronDoubleRight';
import ChevronDoubleLeft from '@spectrum-icons/workflow/ChevronDoubleLeft';
import styles from './ColumnsSetupViewSettings.module.css';

export interface IColumnsSetupViewSettingsProps {
  data: ICustomViewData['columnsSetup'];
  onChange: (data: ICustomViewData['columnsSetup']) => void;
  onMoveToSelected: (itemIds: string[]) => void;
  onMoveToAll: (itemIds: string[]) => void;
  onValidationChange?: (isValid: boolean) => void;
  profiles: Profile[];
  selectedProfileId: string | null;
  onProfileChange: (profileId: string | null) => void;
}

export function ColumnsSetupViewSettings(props: IColumnsSetupViewSettingsProps) {
  const { data, onChange, onMoveToSelected, onMoveToAll, onValidationChange, profiles, selectedProfileId, onProfileChange } = props;
  
  const namesListRef = useRef<INamesListRef>(null);
  const allColumnsRef = useRef<IColumnsSelectorRef>(null);
  const selectedColumnsRef = useRef<IColumnsSelectorRef>(null);

  // Handle names change
  const handleNamesChange = React.useCallback((names: NameInfo[]) => {
    onChange({
      ...data,
      names
    });
  }, [data, onChange]);

  // Handle validation change from NamesList
  const handleValidationChange = React.useCallback((isValid: boolean) => {
    onValidationChange?.(isValid);
  }, [onValidationChange]);

  // Handle view access change
  const handleViewAccessChange = React.useCallback((value: string) => {
    onChange({
      ...data,
      viewAccess: value as 'organization' | 'user' | 'profile'
    });
  }, [data, onChange]);

  // Handle profile selection change
  const handleProfileSelectionChange = React.useCallback((key: Key | null) => {
    onProfileChange(key ? String(key) : null);
  }, [onProfileChange]);


  // Handle move to selected (from all columns to selected columns)
  const handleMoveToSelectedClick = React.useCallback(() => {
    if (allColumnsRef.current) {
      const selectedItems = allColumnsRef.current.getSelectedItems();
      if (selectedItems && selectedItems.length > 0) {
        onMoveToSelected(selectedItems);
        // Clear selection after moving - use setTimeout to ensure state update completes
        setTimeout(() => {
          if (allColumnsRef.current) {
            allColumnsRef.current.clearSelection();
          }
        }, 0);
      }
    }
  }, [onMoveToSelected]);

  // Handle move to all (from selected columns to all columns)
  const handleMoveToAllClick = React.useCallback(() => {
    if (selectedColumnsRef.current) {
      const selectedItems = selectedColumnsRef.current.getSelectedItems();
      if (selectedItems && selectedItems.length > 0) {
        onMoveToAll(selectedItems);
        // Clear selection after moving - use setTimeout to ensure state update completes
        setTimeout(() => {
          if (selectedColumnsRef.current) {
            selectedColumnsRef.current.clearSelection();
          }
        }, 0);
      }
    }
  }, [onMoveToAll]);

  // Handle all columns sections change
  const handleAllColumnsChange = React.useCallback((sections: IColumnSection[]) => {
    onChange({
      ...data,
      allColumns: sections
    });
  }, [data, onChange]);

  // Handle selected columns sections change
  const handleSelectedColumnsChange = React.useCallback((sections: IColumnSection[]) => {
    onChange({
      ...data,
      selectedColumns: sections
    });
  }, [data, onChange]);

  // Handle selected columns reorder - update sections to reflect new item order when in flat list mode
  const handleSelectedColumnsReorder = React.useCallback((reorderedItems: IColumnItem[]) => {
    // When isGroup is false, items are displayed as a flat list
    // Put all reordered items in the first section to maintain the flat list order
    const updatedSections = [...data.selectedColumns];
    if (updatedSections.length === 0) {
      // If no sections exist, create one with the reordered items
      updatedSections.push({
        id: '1',
        label: 'Static Fields',
        items: reorderedItems
      });
    } else {
      // Update first section with reordered items
      if (updatedSections[0]) {
        updatedSections[0] = {
          ...updatedSections[0],
          items: reorderedItems,
        };
        // Clear items from other sections since we're in flat list mode
        for (let i = 1; i < updatedSections.length; i++) {
          updatedSections[i] = {
            ...updatedSections[i],
            items: [],
          };
        }
      }
    }
    handleSelectedColumnsChange(updatedSections);
  }, [data.selectedColumns, handleSelectedColumnsChange]);

  return (
    <Flex direction="column" gap="size-100" UNSAFE_className={styles.columnsSetupViewSettings}>
      <View>
        <NamesList 
          ref={namesListRef}
          defaultValue={data.names}
          onChange={handleNamesChange}
          onValidationChange={handleValidationChange}
        />
      </View>
      
      <Flex direction="row" gap="size-10">
        <View width="50%">
          <ColumnsSelector 
            ref={allColumnsRef}
            height="50vh" 
            width="100%"
            designType={ColumnsSelectorDesignType.DEFAULT}
            selectAllButtonDesignType={AtollonButtonDesignType.LINK}
            headerTitle="All Columns"
            selectAllText="Select All"
            sections={data.allColumns}
            isAllowDnD={false}
            isGroup={true}
            onItemSelectionChanged={(state, item) => {
              // Selection tracking is handled internally by ColumnsSelector
            }}
            onVisibilityChange={(itemId, isVisible) => {
              const updatedSections = data.allColumns.map(section => ({
                ...section,
                items: section.items?.map(item =>
                  item.id === itemId ? { ...item, isVisible } : item
                )
              }));
              handleAllColumnsChange(updatedSections);
            }}
            onFilterChange={(itemId, isFilter) => {
              const updatedSections = data.allColumns.map(section => ({
                ...section,
                items: section.items?.map(item =>
                  item.id === itemId ? { ...item, isFilter } : item
                )
              }));
              handleAllColumnsChange(updatedSections);
            }}
          />
        </View>
        
        <Flex direction="column" gap="size-10" justifyContent="center">
          <View>
            <AtollonButton 
              designType={AtollonButtonDesignType.LINK_POSITIVE}
              onPress={handleMoveToSelectedClick}
            >
              <ChevronDoubleRight />
            </AtollonButton>
          </View>
          <View>
            <AtollonButton 
              designType={AtollonButtonDesignType.LINK_POSITIVE}
              onPress={handleMoveToAllClick}
            >
              <ChevronDoubleLeft />
            </AtollonButton>
          </View>
        </Flex>
        
        <View width="50%">
          <ColumnsSelector 
            ref={selectedColumnsRef}
            height="50vh" 
            width="100%"
            designType={ColumnsSelectorDesignType.POSITIVE}
            selectAllButtonDesignType={AtollonButtonDesignType.LINK_NEGATIVE}
            headerTitle="Selected Columns"
            selectAllText="Select All"
            sections={data.selectedColumns}
            isAllowDnD={true}
            isGroup={false}
            onItemSelectionChanged={(state, item) => {
              // Selection tracking is handled internally by ColumnsSelector
            }}
            onVisibilityChange={(itemId, isVisible) => {
              const updatedSections = data.selectedColumns.map(section => ({
                ...section,
                items: section.items?.map(item =>
                  item.id === itemId ? { ...item, isVisible } : item
                )
              }));
              handleSelectedColumnsChange(updatedSections);
            }}
            onFilterChange={(itemId, isFilter) => {
              const updatedSections = data.selectedColumns.map(section => ({
                ...section,
                items: section.items?.map(item =>
                  item.id === itemId ? { ...item, isFilter } : item
                )
              }));
              handleSelectedColumnsChange(updatedSections);
            }}
            onReOrder={handleSelectedColumnsReorder}
          />
        </View>
      </Flex>

      <Flex direction="column" gap="size-10">
        <RadioGroup 
          label="View access" 
          orientation="horizontal" 
          UNSAFE_className={styles.viewAccess}
          value={data.viewAccess}
          onChange={handleViewAccessChange}
        >
          <Radio value="organization">Organization</Radio>
          <Radio value="profile">Profile</Radio>
          <Radio value="user">Personal</Radio>
        </RadioGroup>

        {data.viewAccess === 'profile' && (
          <ComboBox
            label="Profile"
            selectedKey={selectedProfileId}
            onSelectionChange={handleProfileSelectionChange}
            width="size-3000"
          >
            {profiles.map((profile) => (
              <Item key={profile.id} textValue={profile.name}>
                {profile.name}
              </Item>
            ))}
          </ComboBox>
        )}
      </Flex>
    </Flex>
  );
}

